import { BindCard } from '@components/bindCard/BindCard';
import { Box, Grid, Typography } from '@mui/material';
import { Table } from '@components/table/Table';
import { Button } from '@components/button';
import { useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useSearchQuoteItemCoverages } from '@services/quoteItem/useSearchQuoteItemCoverages';
import { useSaveFields } from '@pages/QuoteDetails/helpers/useSaveFields';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { useQuoteRatingJob } from '@pages/BindQuote/helpers/useQuoteRatingJob';
import { getGLOptionalCoveragesColumns } from '@pages/BindQuote/GLOptionalCoveragesPanel/getGLOptionalCoveragesColumns';
import { glOptionalCoverageIndicators } from '@pages/BindQuote/GLOptionalCoveragesPanel/glOptionalCoverageIndicators';

export const GLOptionalCoveragesPanel = ({
  policyQuoteItem,
  quoteId,
  setIsRating,
}: {
  policyQuoteItem: any;
  quoteId: string;
  setIsRating: (isRating: boolean) => void;
}) => {
  const [disableSave, setDisableSave] = useState(false);

  const activeIndicators = useMemo(
    () =>
      glOptionalCoverageIndicators.filter(
        (indicator) => policyQuoteItem.attributeTypes[indicator.indicatorCodeName]?.attribute?.value === true,
      ),
    [policyQuoteItem],
  );

  const { data: quoteItemCoveragesResult } = useSearchQuoteItemCoverages({ quoteId });
  const quoteItemCoverages = quoteItemCoveragesResult?.items ?? [];

  const { saveFields, isLoading: isSaveFieldsLoading } = useSaveFields({
    quoteId,
    quoteItemId: policyQuoteItem.id,
    fields: activeIndicators.map((indicator) => ({
      attributeTypeId: policyQuoteItem.attributeTypes[indicator.acceptedCodeName]?.id,
      attributeId: policyQuoteItem.attributeTypes[indicator.acceptedCodeName]?.attribute?.id,
    })),
  });

  const { startRating, isCreateRatingLoading } = useQuoteRatingJob({ quoteId, setIsRating });

  const [acceptedByCodeName, setAcceptedByCodeName] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      activeIndicators.map((indicator) => [
        indicator.indicatorCodeName,
        !!policyQuoteItem.attributeTypes[indicator.acceptedCodeName]?.attribute?.value,
      ]),
    ),
  );

  const [setAttributeValue] = useQuoteStore((state) => [state.setAttributeValue], shallow);

  const updateAccepted = (indicatorCodeName: string, accepted: boolean) => {
    setAcceptedByCodeName((prev) => ({ ...prev, [indicatorCodeName]: accepted }));

    const indicator = activeIndicators.find((ind) => ind.indicatorCodeName === indicatorCodeName);
    const acceptedAttributeType = indicator ? policyQuoteItem.attributeTypes[indicator.acceptedCodeName] : undefined;

    // Fall back to the same draft-id convention useSaveFields uses to look up
    // unsaved values (`${quoteItemId}-${attributeTypeId}`) when no
    // QuoteItemAttribute row exists yet for this attribute type. Without this,
    // storing under `undefined` (when .attribute is not yet materialized)
    // silently mismatches useSaveFields' lookup key and the change never
    // makes it into the PATCH body.
    const attributeValueId =
      acceptedAttributeType?.attribute?.id ?? `${policyQuoteItem.id}-${acceptedAttributeType?.id}`;

    setAttributeValue(attributeValueId, {
      isLocked: acceptedAttributeType?.attribute?.isLocked,
      value: accepted,
      valueOverride: null,
    });
  };

  const handleSave = async () => {
    setDisableSave(true);
    try {
      await saveFields([], false);
      await startRating();
    } finally {
      setDisableSave(false);
    }
  };

  const data = activeIndicators.map((indicator) => {
    const coverage = quoteItemCoverages.find((qic) => qic.coverage.coverageName === indicator.coverageName);
    const termPremium = coverage?.quoteItemCoverageAttributes.find(
      (attr) => attr.coverageAttributeType.coverageAttributeCategory === 'TermPremium',
    )?.value;

    return {
      id: indicator.indicatorCodeName,
      indicatorCodeName: indicator.indicatorCodeName,
      label: indicator.label,
      termPremium,
    };
  });

  const columns = getGLOptionalCoveragesColumns({ acceptedByCodeName, updateAccepted });

  const isDirty = activeIndicators.some((indicator) => {
    const savedValue = !!policyQuoteItem.attributeTypes[indicator.acceptedCodeName]?.attribute?.value;
    return acceptedByCodeName[indicator.indicatorCodeName] !== savedValue;
  });

  if (activeIndicators.length === 0) {
    return null;
  }

  return (
    <BindCard>
      {[
        {
          title: 'General Liability Optional Coverages',
          content: (
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <Typography variant='bodySmall'>
                Below are optional coverages that can be added to your policy. Please select the checkbox and save if
                you would like to include the coverage, and you will see it reflected in the estimated premium above.
              </Typography>
              <Grid sx={{ width: '100%', overflowX: 'auto' }}>
                <Table
                  ariaLabel='general liability optional coverages'
                  columns={columns}
                  onLoadData={() => {
                    return {
                      data: {
                        items: data,
                      },
                      isLoading: false,
                      isFetching: false,
                      isError: false,
                    };
                  }}
                  hidePagination
                  noResultsConfig={{ noResultsMessage: 'No optional coverages found' }}
                  sxProps={{
                    tableContainer: {
                      minWidth: { xs: '480px', md: '100%' },
                    },
                  }}
                />
              </Grid>
              <Grid
                container
                justifyContent='flex-end'
                sx={{
                  mt: 2,
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant='outlined'
                  disabled={disableSave || isSaveFieldsLoading || isCreateRatingLoading || !isDirty}
                  isLoading={isSaveFieldsLoading || isCreateRatingLoading}
                  onClick={() => handleSave()}
                >
                  Save
                </Button>
              </Grid>
            </Box>
          ),
        },
      ]}
    </BindCard>
  );
};
